import { Helmet } from "react-helmet-async";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { CTAWhatsApp } from "@/components/public/CTAWhatsApp";
import { BRAND } from "@/lib/brand";

export default function Contato() {
  return (
    <>
      <Helmet>
        <title>{`Contato · ${BRAND.name}`}</title>
        <meta name="description" content={`Entre em contato com a ${BRAND.name}. Atendimento humano por WhatsApp, telefone ou e-mail.`} />
        <link rel="canonical" href="/contato" />
      </Helmet>

      <section className="bg-[#F5F1E8] border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
            Fale com a gente
          </div>
          <h1 className="font-public-title text-4xl lg:text-5xl text-stone-900 leading-[1.05] max-w-3xl">
            Atendimento humano, resposta rápida.
          </h1>
          <p className="mt-5 text-stone-600 text-base max-w-2xl leading-relaxed">
            Preferimos conversa real. Escolha o canal que for mais fácil para você — nosso time responde direto.
          </p>
        </div>
      </section>

      <section data-testid="contato-page" className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ContatoCard
            icon={MessageCircle}
            titulo="WhatsApp"
            texto="Resposta média em minutos."
            action={<CTAWhatsApp variant="primary" label="Abrir conversa" testid="contato-whatsapp" />}
          />
          <ContatoCard
            icon={Phone}
            titulo="Telefone"
            texto="Disponível em horário comercial."
            action={
              <a
                href={`tel:+${BRAND.whatsapp}`}
                data-testid="contato-telefone"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-[#083A6B] hover:bg-[#052B50] text-white text-sm font-medium"
              >
                +55 11 91417-5095
              </a>
            }
          />
          <ContatoCard
            icon={Mail}
            titulo="E-mail"
            texto="Para solicitações formais e cotações."
            action={
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contato-email"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-[#083A6B]/20 text-[#083A6B] hover:bg-[#083A6B]/5 text-sm font-medium"
              >
                {BRAND.email}
              </a>
            }
          />
        </div>

        <div className="mt-14 lg:mt-20 rounded-lg bg-[#F5F1E8] border border-stone-200 p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-3">
              Grupo Hospeda
            </div>
            <h2 className="font-public-title text-2xl lg:text-3xl text-stone-900 leading-tight">
              Operamos as pousadas da {BRAND.name} com time próprio.
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed">
              Isso significa flexibilidade real, preço justo e um ponto de contato
              único para suas dúvidas antes, durante e depois da hospedagem.
            </p>
            <div className="flex items-start gap-2 text-sm text-stone-600 mt-6">
              <MapPin size={14} className="mt-1 flex-shrink-0 text-[#083A6B]" />
              <span>Atendemos Cabreúva, Salto, Itupeva e região.</span>
            </div>
          </div>
          <div className="lg:col-span-2 flex flex-col gap-3">
            <CTAWhatsApp variant="primary" label="Conversar no WhatsApp" testid="contato-cta-wa-bottom" />
            <a
              href={`mailto:${BRAND.email}`}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-[#083A6B]/20 text-[#083A6B] hover:bg-[#083A6B]/5 text-sm font-medium"
            >
              Enviar e-mail
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function ContatoCard({ icon: Icon, titulo, texto, action }) {
  return (
    <div className="flex flex-col gap-4 p-6 rounded-lg bg-[#F5F1E8] border border-stone-200">
      <div className="w-11 h-11 rounded-md bg-[#083A6B]/8 text-[#083A6B] flex items-center justify-center">
        <Icon size={20} strokeWidth={1.6} />
      </div>
      <div>
        <h3 className="font-public-title text-xl text-stone-900 leading-tight">{titulo}</h3>
        <p className="text-sm text-stone-600 mt-2">{texto}</p>
      </div>
      <div className="mt-auto pt-2">{action}</div>
    </div>
  );
}
